import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Receipt } from 'lucide-react';
import { listInvoices, runInvoicing, type RunInvoicingResult } from '../../api/invoicing';
import { useAuth } from '../../auth/useAuth';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card } from '../../components/ui/Card';
import { Table, Thead, Th, Tr, Td } from '../../components/ui/Table';
import { Spinner } from '../../components/ui/Spinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';

function money(value: string) {
  return `£${Number(value).toFixed(2)}`;
}

export function InvoicesListPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [error, setError] = useState<string | null>(null);

  const invoicesQuery = useQuery({ queryKey: ['invoices'], queryFn: () => listInvoices() });

  const runMutation = useMutation({
    mutationFn: () => runInvoicing(),
    onSuccess: (result) => {
      const results = Array.isArray(result) ? result : [result];
      const billed = results.filter((r: RunInvoicingResult) => r.invoice).length;
      showToast(`Invoicing run complete - ${billed} invoice(s) generated.`);
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not run invoicing.')),
  });

  return (
    <div>
      <PageHeader
        title="Invoices"
        description="Consolidated invoices generated for every customer, on their own billing cadence."
        actions={
          user?.isMspAdmin ? (
            <Button size="sm" isLoading={runMutation.isPending} onClick={() => runMutation.mutate()}>
              Run invoicing now
            </Button>
          ) : undefined
        }
      />

      {error && (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      )}

      <Card>
        {invoicesQuery.isLoading ? (
          <div className="py-14 text-center">
            <Spinner />
          </div>
        ) : !invoicesQuery.data || invoicesQuery.data.length === 0 ? (
          <EmptyState icon={Receipt} title="No invoices yet" description="Invoices will appear here once generated." />
        ) : (
          <Table>
            <Thead>
              <tr>
                <Th>Invoice #</Th>
                <Th>Customer</Th>
                <Th>Period</Th>
                <Th>Subtotal</Th>
                <Th>VAT</Th>
                <Th>Total</Th>
                <Th>Generated</Th>
              </tr>
            </Thead>
            <tbody>
              {invoicesQuery.data.map((invoice) => (
                <Tr key={invoice.id}>
                  <Td className="font-mono text-xs text-slate-500">
                    <Link to={`/invoices/${invoice.id}`} className="hover:text-brand-700">
                      {invoice.invoiceNumber}
                    </Link>
                  </Td>
                  <Td className="font-medium text-slate-900">
                    <Link to={`/invoices/${invoice.id}`} className="hover:text-brand-700">
                      {invoice.customerOrganisation?.name ?? '-'}
                    </Link>
                  </Td>
                  <Td>
                    {new Date(invoice.billingPeriodStart).toLocaleDateString()} &ndash;{' '}
                    {new Date(invoice.billingPeriodEnd).toLocaleDateString()}
                  </Td>
                  <Td>{money(invoice.subtotal)}</Td>
                  <Td>{money(invoice.vatTotal)}</Td>
                  <Td className="font-medium text-slate-900">{money(invoice.grandTotal)}</Td>
                  <Td>{new Date(invoice.generatedAt).toLocaleDateString()}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
